import React, { useEffect, useState, createElement, Component } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Copy, FileText, Clock, AlertCircle, RefreshCw, ImageIcon, Link as LinkIcon, Download, Share2, Calendar, User, Heart, MessageSquare, Repeat2, Tag, Monitor, Film, Info, Languages, Languages as LanguagesIcon, Layers, Code, ExternalLink, Play, Quote, BrainCircuit, ChevronDown, ChevronUp, Twitter, Check, Menu, X } from 'lucide-react';
import { getTaskStatus, getTranscript, parseTranscript, getTranscriptWithFormat, getSimilarTranscriptions, getPublicTranscriptPageUrl, getTranscriptionById, deleteTranscription, retryTask } from '../lib/api';
import { TranscriptionResponse, TranscriptionResult, TranscriptFormat } from '../lib/types';
import { TranscriptionCard } from '../components/transcription/TranscriptionCard';
import { ShareTranscript } from '../components/transcription/ShareTranscript';
import { MetaTags } from '../components/MetaTags';
import { ResearchTools } from '../components/research/ResearchTools';
import { TranscriptChatWidget } from '../components/transcription/TranscriptChatWidget';
export function TranscriptionDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const [copied, setCopied] = useState(false);
  const [task, setTask] = useState<TranscriptionResponse | null>(null);
  const [transcript, setTranscript] = useState<TranscriptionResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [processingStartTime, setProcessingStartTime] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState<string>('00:00');
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);
  const [similarTranscriptions, setSimilarTranscriptions] = useState<TranscriptionResponse[]>([]);
  const [showFullTranscript, setShowFullTranscript] = useState(true);
  const [quoteCopied, setQuoteCopied] = useState(false);
  // Mobile specific states
  const [showSecondaryInfo, setShowSecondaryInfo] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showBottomBar, setShowBottomBar] = useState(true);
  // Handle scroll for sticky bottom bar
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setShowBottomBar(false); // Hide on scroll down
      } else {
        setShowBottomBar(true); // Show on scroll up
      }
      setLastScrollY(currentScrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);
  // Helper function to parse TLDR from string to array
  const parseTLDR = (tldrData?: string | string[]): string[] => {
    if (!tldrData) return [];
    // If tldrData is already an array, return it
    if (Array.isArray(tldrData)) {
      return tldrData;
    }
    // Otherwise try to parse it as a JSON string
    try {
      return JSON.parse(tldrData);
    } catch {
      return [];
    }
  };
  const getThumbnailUrl = () => {
    if (task && task.status === 'completed') {
      return task.supabase_thumbnail_url || task.square_thumbnail_url || task.thumbnail_url || task.thumbnail;
    }
    return null;
  };
  const renderPlaceholder = () => {
    if (!task) return null;
    const title = task.title || 'Untitled Video';
    const platform = task.platform || 'TikTok';
    const videoId = task.video_id || task.task_id || 'Unknown';
    return <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-secondary to-background relative" data-id="element-2835">
        <div className="absolute inset-0 opacity-10" data-id="element-2836">
          <div className="absolute top-1/4 left-1/4 w-1/2 h-1/2 rounded-full bg-primary/20" data-id="element-2837"></div>
          <div className="absolute bottom-1/4 right-1/4 w-1/3 h-1/3 rounded-full bg-primary/30" data-id="element-2838"></div>
        </div>
        <div className="z-10 flex flex-col items-center justify-center p-6 text-center" data-id="element-2839">
          <Film size={36} className="text-muted-foreground mb-4" data-id="element-2840" />
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mb-4" data-id="element-2841">
            <span className="text-2xl font-bold text-white" data-id="element-2842">
              {title.substring(0, 2).toUpperCase()}
            </span>
          </div>
          <h3 className="text-white/90 text-lg font-medium mb-2 max-w-xs" data-id="element-2843">
            {title}
          </h3>
          <p className="text-white/60 text-sm mb-1" data-id="element-2844">{platform} Transcript</p>
          <p className="text-white/40 text-xs" data-id="element-2845">
            ID: {videoId.substring(0, 12)}...
          </p>
          {task.duration && <div className="mt-4 bg-white/10 px-3 py-1 rounded-full flex items-center" data-id="element-2846">
              <Clock size={14} className="text-primary mr-2" data-id="element-2847" />
              <span className="text-white/70 text-sm" data-id="element-2848">
                {formatDuration(task.duration)}
              </span>
            </div>}
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1/6 bg-gradient-to-t from-black/40 to-transparent" data-id="element-2849"></div>
      </div>;
  };
  const getMetaTitle = () => {
    if (!task) return 'Transcript | ScribeTok';
    return `TikTok Transcript: "${task.title || 'Untitled Video'}" | ScribeTok`;
  };
  const getMetaDescription = () => {
    if (!task) return 'View the full transcript of this TikTok video on ScribeTok.';
    // Use the quote for the description if available
    if (task.quote) {
      return `"${task.quote}" - See the full transcript for this TikTok video on ScribeTok.`;
    }
    let description = `See the full transcript for this TikTok video`;
    // Add engagement metrics if available
    if (task.like_count || task.comment_count) {
      description += ' (';
      if (task.like_count) description += `${task.like_count.toLocaleString()} likes`;
      if (task.like_count && task.comment_count) description += ', ';
      if (task.comment_count) description += `${task.comment_count.toLocaleString()} comments`;
      description += ')';
    }
    description += ' on ScribeTok.';
    // Add a snippet of the transcript if available
    if (transcript && transcript.text) {
      const snippet = transcript.text.substring(0, 100).trim();
      description += ` "${snippet}${transcript.text.length > 100 ? '...' : ''}"`;
    }
    return description;
  };
  const getMetaImageUrl = () => {
    if (!task) return undefined;
    return getThumbnailUrl() || undefined;
  };
  const getMetaUrl = () => {
    if (!id) return window.location.href;
    return getPublicTranscriptPageUrl(id);
  };
  useEffect(() => {
    const fetchTaskData = async () => {
      if (!id) return;
      try {
        setIsLoading(true);
        setError(null);
        const taskData = await getTaskStatus(id);
        setTask(taskData);
        if (taskData.status === 'completed') {
          try {
            // Always get transcript in text format
            const rawTranscript = (await getTranscriptWithFormat(id, 'text')) as string;
            const parsedTranscript = parseTranscript(rawTranscript);
            setTranscript(parsedTranscript);
            // Get similar transcriptions
            const similar = await getSimilarTranscriptions(id, 4);
            setSimilarTranscriptions(similar);
          } catch (err) {
            console.error('Error fetching transcript data:', err);
          }
          setIsPolling(false);
          setProcessingStartTime(null);
        } else if (taskData.status === 'processing' || taskData.status === 'pending') {
          setIsPolling(true);
          if (!processingStartTime) {
            setProcessingStartTime(Date.now());
          }
        } else if (taskData.status === 'failed') {
          setError(taskData.error || 'Transcription failed');
          setIsPolling(false);
          setProcessingStartTime(null);
        }
      } catch (err) {
        console.error('Error fetching task data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load transcription');
        setIsPolling(false);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTaskData();
    let intervalId: NodeJS.Timeout;
    if (isPolling) {
      intervalId = setInterval(() => {
        fetchTaskData();
      }, 5000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [id, isPolling, processingStartTime]);
  useEffect(() => {
    if (!processingStartTime) return;
    const intervalId = setInterval(() => {
      const now = Date.now();
      const elapsed = Math.floor((now - processingStartTime) / 1000); // elapsed time in seconds
      const minutes = Math.floor(elapsed / 60).toString().padStart(2, '0');
      const seconds = (elapsed % 60).toString().padStart(2, '0');
      setElapsedTime(`${minutes}:${seconds}`);
    }, 1000);
    return () => clearInterval(intervalId);
  }, [processingStartTime]);
  const handleCopyTranscript = () => {
    if (!transcript) return;
    navigator.clipboard.writeText(transcript.text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handleCopyQuote = () => {
    if (!task || !task.quote) return;
    navigator.clipboard.writeText(`"${task.quote}" - ${task.title || 'TikTok Video'}`);
    setQuoteCopied(true);
    setTimeout(() => setQuoteCopied(false), 2000);
  };
  const handleShareQuote = () => {
    if (!task || !task.quote) return;
    const shareText = `"${task.quote}" - ${task.title || 'TikTok Video'}`;
    const shareUrl = getPublicTranscriptPageUrl(task.task_id);
    if (navigator.share) {
      navigator.share({
        text: shareText,
        url: shareUrl
      }).catch(err => {
        console.error('Share failed:', err);
      });
    } else {
      // Fallback if Web Share API is not available
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
    }
  };
  const handleDownload = () => {
    if (!transcript || !task) return;
    const content = transcript.text;
    const blob = new Blob([content], {
      type: 'text/plain'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcript-${task.video_id || task.task_id}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const handleDownloadSrt = () => {
    if (!task) return;
    getTranscriptWithFormat(task.task_id, 'srt').then(content => {
      const blob = new Blob([content as string], {
        type: 'text/srt'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transcript-${task.video_id || task.task_id}.srt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }).catch(err => console.error('Error downloading SRT:', err));
  };
  const handleSharePublicLink = () => {
    if (!task) return;
    const publicUrl = getPublicTranscriptPageUrl(task.task_id);
    navigator.clipboard.writeText(publicUrl);
    alert('Public link copied to clipboard!');
  };
  const handleShare = () => {
    if (navigator.share && transcript && task) {
      navigator.share({
        title: task?.title || 'Video Transcript',
        text: transcript.text.substring(0, 100) + '...',
        url: getPublicTranscriptPageUrl(task.task_id)
      }).catch(err => {
        console.error('Share failed:', err);
        setShowShareOptions(!showShareOptions);
      });
    } else {
      setShowShareOptions(!showShareOptions);
    }
  };
  const handleRetry = async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      setError(null);
      // Call the retry API
      const retryResponse = await retryTask(id);
      console.log('Retry initiated:', retryResponse);
      // Update local state to show processing
      setTask(prev => prev ? {
        ...prev,
        status: 'pending'
      } : null);
      // Start polling for status updates
      setIsPolling(true);
      setProcessingStartTime(Date.now());
    } catch (err) {
      console.error('Error retrying task:', err);
      setError(err instanceof Error ? err.message : 'Failed to retry transcription');
    } finally {
      setIsLoading(false);
    }
  };
  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'Unknown';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (mins < 1) {
      return `${secs}s`;
    }
    return `${mins}m ${secs}s`;
  };
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Unknown';
    // Handle YYYYMMDD format
    if (/^\d{8}$/.test(dateStr)) {
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6);
      const day = dateStr.substring(6, 8);
      return new Date(`${year}-${month}-${day}`).toLocaleDateString();
    }
    // Handle regular date strings
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch (e) {
      return dateStr;
    }
  };
  if (isLoading) {
    return <div className="container py-4 md:py-8 px-4 md:px-6" data-id="element-2850">
        <div className="flex items-center mb-4 md:mb-6" data-id="element-2851">
          <Link to="/app/transcriptions" className="mr-2 md:mr-4" data-id="element-2852">
            <Button variant="ghost" size="sm" className="h-11 px-3 md:h-9 md:px-3" data-id="element-2853">
              <ArrowLeft size={18} className="mr-2" data-id="element-2854" />
              <span className="hidden sm:inline" data-id="element-2855">Back</span>
            </Button>
          </Link>
          <h1 className="text-xl md:text-2xl font-bold truncate" data-id="element-2856">
            Loading Transcription...
          </h1>
        </div>
        <Card className="p-8 md:p-12 flex justify-center items-center" data-id="element-2857">
          <div className="flex flex-col items-center" data-id="element-2858">
            <RefreshCw size={32} className="animate-spin text-primary mb-4" data-id="element-2859" />
            <p className="text-muted-foreground" data-id="element-2860">
              Loading transcription data...
            </p>
          </div>
        </Card>
      </div>;
  }
  if (error || !task) {
    return <div className="container py-4 md:py-8 px-4 md:px-6" data-id="element-2861">
        <div className="flex items-center mb-4 md:mb-6" data-id="element-2862">
          <Link to="/app/transcriptions" className="mr-2 md:mr-4" data-id="element-2863">
            <Button variant="ghost" size="sm" className="h-11 px-3 md:h-9 md:px-3" data-id="element-2864">
              <ArrowLeft size={18} className="mr-2" data-id="element-2865" />
              <span className="hidden sm:inline" data-id="element-2866">Back</span>
            </Button>
          </Link>
          <h1 className="text-xl md:text-2xl font-bold truncate" data-id="element-2867">Error</h1>
        </div>
        <Card className="p-8 md:p-12 border-destructive/20 bg-destructive/5" data-id="element-2868">
          <div className="flex flex-col items-center text-center" data-id="element-2869">
            <AlertCircle size={32} className="text-destructive mb-4" data-id="element-2870" />
            <h2 className="text-xl font-semibold mb-2" data-id="element-2871">
              Failed to Load Transcription
            </h2>
            <p className="text-muted-foreground mb-6" data-id="element-2872">
              {error || 'Transcription not found or an error occurred'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-2873">
              <Link to="/app/transcriptions" data-id="element-2874">
                <Button variant="outline" className="w-full sm:w-auto h-12" data-id="element-2875">
                  Back to Transcriptions
                </Button>
              </Link>
              <Button onClick={handleRetry} className="w-full sm:w-auto h-12" data-id="element-2876">
                Retry
              </Button>
            </div>
          </div>
        </Card>
      </div>;
  }
  if (task && (task.status === 'processing' || task.status === 'pending')) {
    return <div className="container py-4 md:py-8 px-4 md:px-6" data-id="element-2877">
        <div className="flex items-center mb-4 md:mb-6" data-id="element-2878">
          <Link to="/app/transcriptions" className="mr-2 md:mr-4" data-id="element-2879">
            <Button variant="ghost" size="sm" className="h-11 px-3 md:h-9 md:px-3" data-id="element-2880">
              <ArrowLeft size={18} className="mr-2" data-id="element-2881" />
              <span className="hidden sm:inline" data-id="element-2882">Back</span>
            </Button>
          </Link>
          <h1 className="text-xl md:text-2xl font-bold truncate" data-id="element-2883">
            Processing Transcription
          </h1>
        </div>
        <Card className="p-8 md:p-12" data-id="element-2884">
          <div className="flex flex-col items-center text-center" data-id="element-2885">
            <RefreshCw size={32} className="animate-spin text-info mb-4" data-id="element-2886" />
            <h2 className="text-xl font-semibold mb-2" data-id="element-2887">
              Your Video is Being Processed
            </h2>
            <p className="text-muted-foreground mb-2" data-id="element-2888">
              This may take a few minutes depending on the length of the video.
            </p>
            {processingStartTime && <p className="text-info font-medium mb-6" data-id="element-2889">
                Processing time: {elapsedTime}
              </p>}
            <div className="w-full max-w-md mb-6" data-id="element-2890">
              <div className="h-2 bg-secondary rounded-full overflow-hidden" data-id="element-2891">
                <div className="h-2 bg-info rounded-full animate-pulse w-full" data-id="element-2892"></div>
              </div>
            </div>
            <Button variant="outline" onClick={() => window.location.reload()} className="flex items-center h-12" data-id="element-2893">
              <RefreshCw size={16} className="mr-2" data-id="element-2894" />
              Refresh Status
            </Button>
          </div>
        </Card>
      </div>;
  }
  // Parse TLDR if available
  const tldrItems = task.tldr ? parseTLDR(task.tldr) : [];
  const hasInsights = task.quote || tldrItems && tldrItems.length > 0;
  return <div className="container py-4 md:py-8 px-4 md:px-6 pb-24 md:pb-8" data-id="element-2895">
      {task && <MetaTags title={getMetaTitle()} description={getMetaDescription()} imageUrl={getMetaImageUrl()} url={getMetaUrl()} data-id="element-2896" />}

      <div className="flex items-center mb-4 md:mb-6" data-id="element-2897">
        <Link to="/app/transcriptions" className="mr-2 md:mr-4" data-id="element-2898">
          <Button variant="ghost" size="sm" className="h-11 px-3 md:h-9 md:px-3 rounded-full" data-id="element-2899">
            <ArrowLeft size={18} className="mr-1 md:mr-2" data-id="element-2900" />
            <span className="hidden sm:inline" data-id="element-2901">Back</span>
          </Button>
        </Link>
        <h1 className="text-xl md:text-2xl font-bold truncate" data-id="element-2902">
          {task?.title || 'Untitled Video'}
        </h1>
      </div>

      {/* Mobile Sticky Bottom Action Bar - No longer needs chat sheet state check */}
      <div className={`fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-md border-t p-4 z-50 md:hidden transition-transform duration-300 ${showBottomBar ? 'translate-y-0' : 'translate-y-full'}`} data-id="element-2903">
        <div className="flex gap-3 justify-between max-w-md mx-auto" data-id="element-2904">
          <Button variant="outline" className="flex-1 justify-center items-center rounded-full h-12" onClick={handleCopyTranscript} disabled={!transcript} data-id="element-2905">
            <Copy size={18} className="mr-2" data-id="element-2906" />
            Copy
          </Button>
          <Button variant="outline" className="flex-1 justify-center items-center rounded-full h-12" onClick={handleDownload} disabled={!transcript} data-id="element-2907">
            <Download size={18} className="mr-2" data-id="element-2908" />
            Save
          </Button>
          <Button variant="default" className="flex-1 justify-center items-center rounded-full h-12" onClick={handleShare} disabled={!transcript} data-id="element-2909">
            <Share2 size={18} className="mr-2" data-id="element-2910" />
            Share
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6" data-id="element-2911">
        {/* Left Column (Video & Metadata) */}
        <div className="lg:col-span-1 space-y-4 md:space-y-6" data-id="element-2912">
          {/* Video Preview Card - Always Visible */}
          <Card className="overflow-hidden border-0 shadow-md" data-id="element-2913">
            <div className="w-full aspect-video bg-card flex items-center justify-center overflow-hidden relative" data-id="element-2914">
              {task.status === 'completed' ? <div className="relative w-full h-full" data-id="element-2915">
                  {getThumbnailUrl() ? <>
                      {/* Blurred background layer */}
                      <div className="absolute inset-0 z-0" style={{
                  backgroundImage: `url(${getThumbnailUrl()})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  filter: 'blur(40px)',
                  transform: 'scale(1.1)'
                }} data-id="element-2916" />

                      {/* Dark overlay for better contrast */}
                      <div className="absolute inset-0 bg-black/40 z-[1]" data-id="element-2917" />

                      {/* Main image - fills the card */}
                      <img src={getThumbnailUrl() || ''} alt={task.title || 'Video thumbnail'} className="absolute inset-0 w-full h-full object-cover z-[2]" onError={e => {
                  e.currentTarget.style.display = 'none';
                  const container = e.currentTarget.parentElement;
                  if (container) {
                    const placeholder = container.querySelector('.placeholder-fallback');
                    if (placeholder) {
                      ;
                      (placeholder as HTMLElement).style.display = 'flex';
                    }
                  }
                }} data-id="element-2918" />

                      {/* Fallback placeholder (hidden by default) */}
                      <div className="placeholder-fallback absolute inset-0 hidden w-full h-full flex-col items-center justify-center bg-gradient-to-br from-secondary to-background z-[2]" data-id="element-2919">
                        <div className="z-10 flex flex-col items-center justify-center p-6 text-center" data-id="element-2920">
                          <Film size={36} className="text-muted-foreground mb-4" data-id="element-2921" />
                          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mb-4" data-id="element-2922">
                            <span className="text-2xl font-bold text-white" data-id="element-2923">
                              {(task.title || 'UV').substring(0, 2).toUpperCase()}
                            </span>
                          </div>
                          <h3 className="text-white/90 text-lg font-medium mb-2 max-w-xs" data-id="element-2924">
                            {task.title || 'Untitled Video'}
                          </h3>
                        </div>
                      </div>
                    </> : renderPlaceholder()}

                  {/* Play button overlay */}
                  {task.url && <div className="absolute inset-0 flex items-center justify-center z-[20] cursor-pointer bg-black/20 hover:bg-black/30 transition-colors" onClick={() => {
                const videoContainer = document.createElement('div');
                videoContainer.className = 'fixed inset-0 bg-black/90 flex items-center justify-center z-50';
                videoContainer.style.backdropFilter = 'blur(5px)';
                const videoElement = document.createElement('video');
                videoElement.className = 'w-full max-w-4xl max-h-[90vh]';
                videoElement.controls = true;
                videoElement.autoplay = true;
                videoElement.src = task.url;
                const closeButton = document.createElement('button');
                closeButton.className = 'absolute top-4 right-4 text-white bg-white/10 rounded-full p-3 hover:bg-white/20 transition-colors';
                closeButton.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>`;
                closeButton.onclick = () => {
                  document.body.removeChild(videoContainer);
                };
                videoContainer.appendChild(videoElement);
                videoContainer.appendChild(closeButton);
                document.body.appendChild(videoContainer);
                videoContainer.onclick = e => {
                  if (e.target === videoContainer) {
                    document.body.removeChild(videoContainer);
                  }
                };
              }} data-id="element-2925">
                      <div className="w-16 h-16 rounded-full bg-primary/90 flex items-center justify-center hover:bg-primary transition-colors shadow-lg backdrop-blur-sm" data-id="element-2926">
                        <Play size={32} className="text-white ml-1" data-id="element-2927" />
                      </div>
                    </div>}
                </div> : <div className="flex flex-col items-center justify-center" data-id="element-2928">
                  <ImageIcon size={24} className="text-muted-foreground mb-2" data-id="element-2929" />
                  <span className="text-muted-foreground font-medium" data-id="element-2930">
                    {task.video_id ? `Video ID: ${task.video_id}` : 'Thumbnail not available'}
                  </span>
                </div>}
            </div>
            <CardContent className="p-5" data-id="element-2931">
              <h3 className="font-semibold text-lg md:text-xl mb-3 leading-snug" data-id="element-2932">
                {task.title || 'Untitled Video'}
              </h3>
              {/* Basic Video Info */}
              <div className="flex flex-wrap gap-3 text-sm text-muted-foreground mb-4" data-id="element-2933">
                <span className="flex items-center bg-secondary/50 px-2 py-1 rounded-md" data-id="element-2934">
                  <Clock size={14} className="mr-1.5" data-id="element-2935" />
                  {task.duration ? formatDuration(task.duration) : transcript && transcript.segments.length > 0 ? `${Math.floor(transcript.segments[transcript.segments.length - 1].end / 60)}:${Math.floor(transcript.segments[transcript.segments.length - 1].end % 60).toString().padStart(2, '0')}` : '00:00'}
                </span>
                <span className="flex items-center bg-secondary/50 px-2 py-1 rounded-md" data-id="element-2936">
                  <Calendar size={14} className="mr-1.5" data-id="element-2937" />
                  {task.upload_date ? formatDate(task.upload_date) : new Date(task.created_at).toLocaleDateString()}
                </span>
                {task.platform && <span className="flex items-center bg-secondary/50 px-2 py-1 rounded-md capitalize" data-id="element-2938">
                    <Film size={14} className="mr-1.5" data-id="element-2939" />
                    {task.platform}
                  </span>}
              </div>

              {/* View Original Button */}
              <div className="space-y-3" data-id="element-2940">
                {(() => {
                // Prefer the original submitted URL, then uploader profile URL
                // Do NOT construct from video_id alone — tiktok.com/video/ID doesn't reliably work
                const videoUrl = task.url || task.uploader_url || null;
                return videoUrl ? <>
                      <a href={videoUrl} target="_blank" rel="noopener noreferrer" className="block w-full" data-id="element-2941">
                        <Button variant="tiktok" className="w-full flex items-center justify-center h-12 text-base font-medium" data-id="element-2942">
                          <LinkIcon size={18} className="mr-2" data-id="element-2943" />
                          View Original Video
                        </Button>
                      </a>
                      <div className="flex items-center gap-2 p-2 bg-secondary/30 rounded-lg border border-border/30" data-id="element-2944">
                        <ExternalLink size={13} className="text-muted-foreground flex-shrink-0" data-id="element-2945" />
                        <span className="text-xs text-muted-foreground truncate flex-1 font-mono" data-id="element-2946">
                          {videoUrl}
                        </span>
                        <button onClick={() => navigator.clipboard.writeText(videoUrl)} className="text-muted-foreground hover:text-foreground flex-shrink-0 p-1 rounded" title="Copy URL" data-id="element-2947">
                          <Copy size={13} data-id="element-2948" />
                        </button>
                      </div>
                    </> : null;
              })()}
              </div>
            </CardContent>
          </Card>

          {/* Secondary Info Wrapper - Collapsible on Mobile, Visible on Desktop */}
          <div className="space-y-4 md:space-y-6" data-id="element-2949">
            {/* Mobile Toggle for Secondary Info */}
            <Button variant="outline" className="w-full flex justify-between items-center md:hidden h-14 text-base" onClick={() => setShowSecondaryInfo(!showSecondaryInfo)} data-id="element-2950">
              <span className="flex items-center" data-id="element-2951">
                <Info size={18} className="mr-2 text-primary" data-id="element-2952" />
                Video Details & Stats
              </span>
              {showSecondaryInfo ? <ChevronUp size={18} data-id="element-2953" /> : <ChevronDown size={18} data-id="element-2954" />}
            </Button>

            <div className={`space-y-4 md:space-y-6 ${showSecondaryInfo ? 'block' : 'hidden md:block'}`} data-id="element-2955">
              {/* Creator Info Card */}
              {(task.channel || task.uploader || task.channel_id) && <Card data-id="element-2956">
                  <CardContent className="p-5" data-id="element-2957">
                    <h3 className="font-medium mb-4 flex items-center text-base" data-id="element-2958">
                      <User size={18} className="mr-2 text-primary" data-id="element-2959" />
                      Creator Information
                    </h3>
                    <div className="space-y-3" data-id="element-2960">
                      {task.uploader && <div className="flex justify-between items-center p-2 bg-secondary/30 rounded-lg" data-id="element-2961">
                          <span className="text-sm text-muted-foreground" data-id="element-2962">
                            Username
                          </span>
                          <span className="text-sm font-medium truncate max-w-[200px]" data-id="element-2963">
                            {task.uploader}
                          </span>
                        </div>}
                      {task.channel && <div className="flex justify-between items-center p-2 bg-secondary/30 rounded-lg" data-id="element-2964">
                          <span className="text-sm text-muted-foreground" data-id="element-2965">
                            Channel
                          </span>
                          <span className="text-sm font-medium truncate max-w-[200px]" data-id="element-2966">
                            {task.channel}
                          </span>
                        </div>}
                      {task.uploader_url && <div className="mt-2" data-id="element-2967">
                          <a href={task.uploader_url} target="_blank" rel="noopener noreferrer" className="text-primary text-sm hover:underline flex items-center justify-center p-2 bg-primary/5 rounded-lg font-medium" data-id="element-2968">
                            <LinkIcon size={14} className="mr-2" data-id="element-2969" />
                            View Creator Profile
                          </a>
                        </div>}
                    </div>
                  </CardContent>
                </Card>}

              {/* Engagement Metrics Card */}
              <Card data-id="element-2970">
                <CardContent className="p-5" data-id="element-2971">
                  <h3 className="font-medium mb-4 flex items-center text-base" data-id="element-2972">
                    <Heart size={18} className="mr-2 text-primary" data-id="element-2973" />
                    Engagement Metrics
                  </h3>
                  {task.like_count || task.comment_count || task.repost_count || task.view_count ? <div className="grid grid-cols-3 gap-3 text-center" data-id="element-2974">
                      <div className="p-3 bg-secondary/50 rounded-xl" data-id="element-2975">
                        <div className="flex justify-center mb-1.5" data-id="element-2976">
                          <Heart size={20} className="text-primary" data-id="element-2977" />
                        </div>
                        <span className="block text-base font-bold" data-id="element-2978">
                          {task.like_count?.toLocaleString() || '—'}
                        </span>
                        <span className="block text-xs text-muted-foreground mt-0.5" data-id="element-2979">
                          Likes
                        </span>
                      </div>
                      <div className="p-3 bg-secondary/50 rounded-xl" data-id="element-2980">
                        <div className="flex justify-center mb-1.5" data-id="element-2981">
                          <MessageSquare size={20} className="text-primary" data-id="element-2982" />
                        </div>
                        <span className="block text-base font-bold" data-id="element-2983">
                          {task.comment_count?.toLocaleString() || '—'}
                        </span>
                        <span className="block text-xs text-muted-foreground mt-0.5" data-id="element-2984">
                          Comments
                        </span>
                      </div>
                      <div className="p-3 bg-secondary/50 rounded-xl" data-id="element-2985">
                        <div className="flex justify-center mb-1.5" data-id="element-2986">
                          <Repeat2 size={20} className="text-primary" data-id="element-2987" />
                        </div>
                        <span className="block text-base font-bold" data-id="element-2988">
                          {task.repost_count?.toLocaleString() || '—'}
                        </span>
                        <span className="block text-xs text-muted-foreground mt-0.5" data-id="element-2989">
                          Reposts
                        </span>
                      </div>
                    </div> : <div className="text-center py-4 text-sm text-muted-foreground" data-id="element-2990">
                      <MessageSquare size={24} className="mx-auto mb-2 opacity-30" data-id="element-2991" />
                      <p data-id="element-2992">Engagement data not available for this video.</p>
                      <p className="text-xs mt-1 opacity-70" data-id="element-2993">
                        This may be a private video or the platform didn't
                        return metrics.
                      </p>
                    </div>}
                </CardContent>
              </Card>

              {/* Tags Card */}
              {task.auto_tags && task.auto_tags.length > 0 && <Card data-id="element-2994">
                  <CardContent className="p-5" data-id="element-2995">
                    <h3 className="font-medium mb-3 flex items-center text-base" data-id="element-2996">
                      <Tag size={18} className="mr-2 text-primary" data-id="element-2997" />
                      Tags
                    </h3>
                    <div className="flex flex-wrap gap-2" data-id="element-2998">
                      {task.auto_tags.map((tag, index) => <span key={index} className="inline-block px-3 py-1.5 bg-secondary text-secondary-foreground rounded-full text-sm" data-id="element-2999">
                          #{tag}
                        </span>)}
                    </div>
                  </CardContent>
                </Card>}

              {/* Technical Details Card */}
              {(task.resolution || task.width || task.height || task.vcodec || task.acodec) && <Card data-id="element-3000">
                  <CardContent className="p-5" data-id="element-3001">
                    <div className="flex justify-between items-center mb-3" data-id="element-3002">
                      <h3 className="font-medium flex items-center text-base" data-id="element-3003">
                        <Monitor size={18} className="mr-2 text-muted-foreground" data-id="element-3004" />
                        Technical Details
                      </h3>
                      <Button variant="ghost" size="sm" onClick={() => setShowTechnicalDetails(!showTechnicalDetails)} className="h-8 px-2 text-xs" data-id="element-3005">
                        {showTechnicalDetails ? 'Hide' : 'Show'}
                      </Button>
                    </div>
                    {showTechnicalDetails ? <div className="space-y-2 text-sm" data-id="element-3006">
                        {task.resolution && <div className="flex justify-between p-2 bg-secondary/20 rounded" data-id="element-3007">
                            <span className="text-muted-foreground" data-id="element-3008">
                              Resolution
                            </span>
                            <span className="font-mono" data-id="element-3009">{task.resolution}</span>
                          </div>}
                        {(task.width || task.height) && <div className="flex justify-between p-2 bg-secondary/20 rounded" data-id="element-3010">
                            <span className="text-muted-foreground" data-id="element-3011">
                              Dimensions
                            </span>
                            <span className="font-mono" data-id="element-3012">
                              {task.width || '?'}×{task.height || '?'}
                            </span>
                          </div>}
                        {task.aspect_ratio && <div className="flex justify-between p-2 bg-secondary/20 rounded" data-id="element-3013">
                            <span className="text-muted-foreground" data-id="element-3014">
                              Aspect Ratio
                            </span>
                            <span className="font-mono" data-id="element-3015">
                              {task.aspect_ratio.toFixed(2)}
                            </span>
                          </div>}
                        {task.filesize && <div className="flex justify-between p-2 bg-secondary/20 rounded" data-id="element-3016">
                            <span className="text-muted-foreground" data-id="element-3017">
                              File Size
                            </span>
                            <span className="font-mono" data-id="element-3018">
                              {(task.filesize / (1024 * 1024)).toFixed(2)} MB
                            </span>
                          </div>}
                      </div> : <div className="text-center py-2 text-sm text-muted-foreground" data-id="element-3019">
                        Click "Show" to view technical specifications
                      </div>}
                  </CardContent>
                </Card>}

              {/* Desktop Download Options */}
              <Card className="hidden md:block" data-id="element-3020">
                <CardContent className="p-5" data-id="element-3021">
                  <h3 className="font-medium mb-3" data-id="element-3022">Download Transcript</h3>
                  <div className="space-y-2" data-id="element-3023">
                    <Button variant="outline" className="w-full justify-start rounded-lg h-11" onClick={handleDownload} disabled={!transcript} data-id="element-3024">
                      <FileText size={18} className="mr-2" data-id="element-3025" />
                      Download as TXT
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Right Column (Main Content) */}
        <div className="lg:col-span-2 space-y-4 md:space-y-6" data-id="element-3026">
          {/* Chat Widget - Show if we have ANY content to chat about (including just title) */}
          {task && task.task_id && task.status === 'completed' && (transcript?.text || task.description || task.title || task.quote || task.tldr && parseTLDR(task.tldr).length > 0) && <TranscriptChatWidget transcriptId={task.task_id} transcript={{
          content: transcript?.text || task.description || task.title || '',
          title: task.title || undefined,
          description: task.description || undefined,
          channel_name: task.channel || task.uploader || undefined,
          platform: task.platform || undefined,
          view_count: task.view_count || undefined,
          like_count: task.like_count || undefined,
          comment_count: task.comment_count || undefined,
          repost_count: task.repost_count || undefined,
          upload_date: task.upload_date || undefined,
          duration: task.duration || undefined,
          quote: task.quote || undefined,
          tldr: tldrItems
        }} data-id="element-3027" />}

          {/* Insights Card with Quote and TLDR */}
          {hasInsights && <Card className="border-0 shadow-sm md:border md:shadow-sm" data-id="element-3028">
              <div className="flex items-center justify-between p-4 md:p-5 border-b border-border/40" data-id="element-3029">
                <h2 className="font-semibold text-base md:text-lg flex items-center" data-id="element-3030">
                  <BrainCircuit size={20} className="mr-2 text-primary" data-id="element-3031" />
                  Insights
                </h2>
                {task.quote && <div className="flex items-center space-x-1" data-id="element-3032">
                    <Button variant="ghost" size="sm" onClick={handleCopyQuote} className="h-9 px-3 text-xs rounded-full" data-id="element-3033">
                      {quoteCopied ? <Check size={16} className="mr-1" data-id="element-3034" /> : <Copy size={16} className="mr-1" data-id="element-3035" />}
                      {quoteCopied ? 'Copied' : 'Copy'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={handleShareQuote} className="h-9 px-3 text-xs rounded-full" data-id="element-3036">
                      <Twitter size={16} className="mr-1" data-id="element-3037" />
                      Share
                    </Button>
                  </div>}
              </div>
              <div className="p-5 md:p-6" data-id="element-3038">
                {/* Quote Section */}
                {task.quote && <div className="mb-6" data-id="element-3039">
                    <div className="flex items-center mb-3" data-id="element-3040">
                      <Quote size={18} className="mr-2 text-primary" data-id="element-3041" />
                      <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground" data-id="element-3042">
                        Key Quote
                      </h3>
                    </div>
                    <blockquote className="border-l-4 border-primary/30 pl-4 py-1 text-lg md:text-xl font-medium italic text-foreground/90 leading-relaxed" data-id="element-3043">
                      "{task.quote}"
                    </blockquote>
                  </div>}
                {/* TLDR Section */}
                {tldrItems && tldrItems.length > 0 && <div data-id="element-3044">
                    <div className="flex items-center mb-3" data-id="element-3045">
                      <FileText size={18} className="mr-2 text-primary" data-id="element-3046" />
                      <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground" data-id="element-3047">
                        Key Takeaways
                      </h3>
                    </div>
                    <ul className="space-y-3 pl-1" data-id="element-3048">
                      {tldrItems.map((item, index) => <li key={index} className="flex items-start" data-id="element-3049">
                          <span className="inline-flex flex-shrink-0 bg-primary/10 text-primary font-bold rounded-full w-6 h-6 items-center justify-center text-xs mr-3 mt-0.5" data-id="element-3050">
                            {index + 1}
                          </span>
                          <span className="text-base leading-relaxed" data-id="element-3051">
                            {item}
                          </span>
                        </li>)}
                    </ul>
                  </div>}
              </div>
            </Card>}

          {/* Description Card (if available) */}
          {task?.description && <Card data-id="element-3052">
              <div className="flex items-center justify-between p-4 md:p-5 border-b border-border/40" data-id="element-3053">
                <h2 className="font-semibold text-base md:text-lg flex items-center" data-id="element-3054">
                  <Info size={20} className="mr-2 text-primary" data-id="element-3055" />
                  Description
                </h2>
              </div>
              <div className="p-5" data-id="element-3056">
                <p className="text-base whitespace-pre-wrap leading-relaxed text-muted-foreground" data-id="element-3057">
                  {task.description}
                </p>
              </div>
            </Card>}

          {/* Transcript Card */}
          <Card data-id="element-3058">
            <div className="flex items-center justify-between p-4 md:p-5 border-b border-border/40 sticky top-0 bg-background/95 backdrop-blur z-10" data-id="element-3059">
              <h2 className="font-semibold text-base md:text-lg flex items-center" data-id="element-3060">
                <FileText size={20} className="mr-2 text-primary" data-id="element-3061" />
                Full Transcript
              </h2>
              <div className="flex items-center" data-id="element-3062">
                <Button variant="ghost" size="sm" onClick={() => setShowFullTranscript(!showFullTranscript)} className="text-primary h-9 px-3 rounded-full mr-1" data-id="element-3063">
                  {showFullTranscript ? <ChevronUp size={18} className="mr-1" data-id="element-3064" /> : <ChevronDown size={18} className="mr-1" data-id="element-3065" />}
                  {showFullTranscript ? 'Hide' : 'Show'}
                </Button>
                <Button variant="ghost" size="sm" onClick={handleCopyTranscript} className="text-primary h-9 px-3 rounded-full hidden md:flex" disabled={!transcript} data-id="element-3066">
                  <Copy size={18} className="mr-1" data-id="element-3067" />
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>
            </div>
            {transcript && transcript.segments.length > 0 ? <>
                {!showFullTranscript ? <div className="p-6 md:p-8" data-id="element-3068">
                    {/* Transcript Preview Section */}
                    <div className="mb-6" data-id="element-3069">
                      <div className="flex items-center gap-2 mb-3" data-id="element-3070">
                        <FileText size={16} className="text-muted-foreground" data-id="element-3071" />
                        <h3 className="text-sm font-medium text-muted-foreground" data-id="element-3072">
                          Transcript preview
                        </h3>
                      </div>
                      <p className="text-base leading-relaxed text-foreground/80 line-clamp-3" data-id="element-3073">
                        {transcript.text.substring(0, 200)}...
                      </p>
                    </div>

                    {/* Stats */}
                    <div className="text-center mb-6" data-id="element-3074">
                      <p className="text-sm text-muted-foreground" data-id="element-3075">
                        The full transcript contains{' '}
                        {transcript.segments.length} segments and approximately{' '}
                        {Math.round(transcript.text.length / 5)} words.
                      </p>
                    </div>

                    {/* Show Button */}
                    <div className="text-center" data-id="element-3076">
                      <Button variant="outline" onClick={() => setShowFullTranscript(true)} className="rounded-full h-11 px-6" data-id="element-3077">
                        <ChevronDown size={18} className="mr-2" data-id="element-3078" />
                        Show Full Transcript
                      </Button>
                    </div>
                  </div> : <div className="p-5 md:p-6 max-h-[600px] md:max-h-[800px] overflow-y-auto scrollbar-hide" data-id="element-3079">
                    <div className="transcript-container space-y-6" data-id="element-3080">
                      {transcript.segments.map(segment => <div key={segment.id} className="group" data-id="element-3081">
                          <div className="text-xs font-mono text-muted-foreground mb-1 opacity-70 group-hover:opacity-100 transition-opacity" data-id="element-3082">
                            <span data-id="element-3083">
                              {Math.floor(segment.start / 60)}:
                              {Math.floor(segment.start % 60).toString().padStart(2, '0')}{' '}
                              -{Math.floor(segment.end / 60)}:
                              {Math.floor(segment.end % 60).toString().padStart(2, '0')}
                            </span>
                          </div>
                          <p className="font-medium text-lg leading-relaxed text-foreground/90" data-id="element-3084">
                            {segment.text}
                          </p>
                        </div>)}
                    </div>
                  </div>}
              </> : <div className="p-8 md:p-12 text-center" data-id="element-3085">
                <p className="text-muted-foreground" data-id="element-3086">
                  {transcript ? 'No transcript segments available.' : 'Transcript data not available.'}
                </p>
              </div>}
          </Card>

          {/* Share Transcript Card */}
          {task && task.task_id && <Card data-id="element-3087">
              <CardContent className="p-5" data-id="element-3088">
                <ShareTranscript taskId={task.task_id} title={task.title || undefined} data-id="element-3089" />
              </CardContent>
            </Card>}

          {/* Research Tools Section */}
          {task && task.task_id && task.status === 'completed' && <ResearchTools taskId={task.task_id} videoId={task.video_id || undefined} data-id="element-3090" />}

          {/* Similar Transcriptions */}
          {similarTranscriptions.length > 0 && <Card className="mt-6" data-id="element-3091">
              <div className="flex items-center justify-between p-4 md:p-5 border-b border-border/40" data-id="element-3092">
                <h2 className="font-semibold text-base md:text-lg" data-id="element-3093">
                  Similar Content
                </h2>
              </div>
              <div className="p-4 md:p-5" data-id="element-3094">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3095">
                  {similarTranscriptions.map(transcription => <div key={transcription.task_id} className="h-full" data-id="element-3096">
                      <TranscriptionCard transcription={transcription} data-id="element-3097" />
                    </div>)}
                </div>
              </div>
            </Card>}
        </div>
      </div>
    </div>;
}